import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useVisitorTracking } from './useVisitorTracking';

const SCROLL_MILESTONES = [25, 50, 75, 100] as const;

/**
 * Hook that automatically tracks scroll depth milestones (25%, 50%, 75%, 100%)
 * Uses IntersectionObserver for performance - no scroll event spam
 * Each milestone fires only once per page view and resets on route change
 */
export const useScrollDepthTracking = () => {
  const location = useLocation();
  const { trackScrollDepth } = useVisitorTracking();
  const trackedMilestonesRef = useRef<Set<number>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const markersRef = useRef<HTMLDivElement[]>([]);

  // Create scroll depth markers
  const createMarkers = useCallback(() => {
    // Remove existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Create new markers for each milestone
    SCROLL_MILESTONES.forEach(milestone => {
      const marker = document.createElement('div');
      marker.id = `scroll-marker-${milestone}`;
      marker.style.cssText = `
        position: absolute;
        left: 0;
        right: 0;
        height: 1px;
        pointer-events: none;
        visibility: hidden;
      `;
      // Position marker at the percentage point of the document
      marker.style.top = `${milestone}%`;
      document.body.appendChild(marker);
      markersRef.current.push(marker);
    });
  }, []);

  // Setup intersection observer
  const setupObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const markerId = entry.target.id;
            const milestone = parseInt(markerId.replace('scroll-marker-', ''), 10);
            
            if (!trackedMilestonesRef.current.has(milestone)) {
              trackedMilestonesRef.current.add(milestone);
              trackScrollDepth(milestone);
              // Meta Pixel ScrollDepth removed — noise event that dilutes Lead signal
            }
          }
        });
      },
      {
        root: null, // viewport
        rootMargin: '0px',
        threshold: 0,
      }
    );

    // Observe all markers
    markersRef.current.forEach(marker => {
      observerRef.current?.observe(marker);
    });
  }, [trackScrollDepth, location.pathname]);

  // Phase 9: Reset and reinitialize on route change with deferred initialization
  useEffect(() => {
    // Reset tracked milestones
    trackedMilestonesRef.current = new Set();

    // Defer scroll tracking 2 seconds to not compete with LCP
    const timer = setTimeout(() => {
      createMarkers();
      setupObserver();
    }, 2000);

    return () => {
      clearTimeout(timer);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
    };
  }, [location.pathname, createMarkers, setupObserver]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      markersRef.current.forEach(marker => marker.remove());
    };
  }, []);
};

export default useScrollDepthTracking;
